/**
 * Client 360 — the primary staff view of a client.
 *
 * Layout (left-to-right at ≥ 900 px):
 *   [Left sidebar 260 px] | [Main content 1fr] | [Call guide panel 340 px, collapsible]
 *
 * Data loaded in parallel on mount:
 *   · GET /staff/clients/{id}/profile      — identity + KYC
 *   · GET /portfolio/clients/{id}/summary  — financial snapshot + maturities
 *   · GET /staff/clients/{id}/cases        — open cases
 *   · GET /staff/clients/{id}/interactions — recent + pinned interactions
 *
 * Quick-action buttons open action pages in a new tab with ↗ so the agent
 * keeps this context visible during the interaction.
 *
 * The "Statements" section is embedded at the bottom (collapsible) rather than
 * a separate page to preserve the original staff workflow.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import NavCorrectionsCard from '../../components/client/NavCorrectionsCard';
import { useParams } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import ClientComplianceActions from '../../components/staff/ClientComplianceActions';
import CallGuidePanel from '../../components/agent/CallGuidePanel';
import NewTaskModal from '../../components/agent/NewTaskModal';
import ProposeProductModal from '../../components/staff/ProposeProductModal';
import AgentDepositNotice from '../../components/agent/AgentDepositNotice';
import portfolioService from '../../services/portfolio.service';
import { productOptions, type ProductOption } from '../../lib/productOptions';
import staffService, { type ClientProfile, type ClientKycStatus } from '../../services/staff.service';
import interactionsService, {
  type ClientCase,
  type Interaction,
} from '../../services/interactions.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime, formatMoney } from '../../lib/datetime';
import { completeAfter, openTermKey } from '../../components/client/openTerms.helpers';
import { pickCurrencyBalance } from '../../lib/portfolioSummary.helpers';
import { humanize } from '../../lib/stringCase';
import { colors, gradients } from '../../styles/theme';
import type { PortfolioSummary, Statement, StatementFilters } from '../../types/finance';

// ─── Helpers ──────────────────────────────────────────────────────

function kindLabel(k: string): string {
  const MAP: Record<string, string> = {
    phone_outbound: 'Outbound call',
    phone_inbound: 'Inbound call',
    email_outbound: 'Email sent',
    email_inbound: 'Email received',
  };
  return MAP[k] ?? k;
}

function outcomeLabel(o: string): string {
  const MAP: Record<string, string> = {
    connected: 'Connected',
    voicemail_left: 'Voicemail left',
    no_answer: 'No answer',
    busy: 'Busy',
    wrong_number: 'Wrong number',
    client_hung_up: 'Client hung up',
    callback_scheduled: 'Callback scheduled',
  };
  return MAP[o] ?? o;
}

function kycBadge(kyc: ClientKycStatus): { label: string; bg: string; color: string } {
  if (kyc.is_expired) {
    return { label: 'KYC Expired', bg: colors.dangerBg, color: colors.danger };
  }
  if (kyc.revalidation_required) {
    return { label: `KYC expiring in ${kyc.days_to_expiry}d`, bg: colors.warningBg, color: colors.warning };
  }
  return { label: 'KYC Valid', bg: colors.successBg, color: colors.successText };
}

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Identity header ──────────────────────────────────────────────

interface IdentityHeaderProps {
  readonly profile: ClientProfile;
}

function IdentityHeader({ profile }: IdentityHeaderProps): JSX.Element {
  const fullName = `${profile.first_name} ${profile.last_name}`.trim();
  const kyc = profile.kyc_status;
  const kycInfo = kyc ? kycBadge(kyc) : null;

  return (
    <div style={{ background: gradients.navy, borderRadius: 14, padding: '20px 24px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>{fullName}</h1>
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {profile.email && <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{profile.email}</span>}
          {profile.phone && (
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
              {profile.phone}
              {/* An agent about to trust a voice on this line needs to know whether
                  anyone ever proved it answers. Absence of proof is the finding,
                  so the unproven state is the one that is labelled. */}
              <span style={{
                marginLeft: 6, fontSize: 10, fontWeight: 700, borderRadius: 5,
                padding: '2px 6px',
                background: profile.phone_verified ? 'rgba(16,185,129,0.18)' : 'rgba(251,191,36,0.2)',
                color: profile.phone_verified ? '#6EE7B7' : '#FCD34D',
              }}>
                {profile.phone_verified ? 'VERIFIED' : 'NOT VERIFIED'}
              </span>
            </span>
          )}
          {profile.phone_change_hold_until && (
            <span style={{
              fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px',
              background: 'rgba(251,191,36,0.2)', color: '#FCD34D',
            }}>
              NUMBER CHANGED — MONEY OUT HELD UNTIL {formatDate(profile.phone_change_hold_until)}
            </span>
          )}
          {profile.former_client_since && (
            <span style={{
              fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 8px',
              background: 'rgba(148,163,184,0.2)', color: '#CBD5E1',
            }}>
              Former client since {formatDate(profile.former_client_since)}
            </span>
          )}
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            Member since {formatDate(profile.member_since)}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        {profile.is_on_roster && (
          <span style={{ fontSize: 11, fontWeight: 700, background: colors.gold, color: colors.navy, borderRadius: 6, padding: '3px 10px' }}>
            Your client
          </span>
        )}
        {!profile.is_on_roster && profile.assigned_agent_name && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
            Assigned to {profile.assigned_agent_name}
          </span>
        )}
        {!profile.is_on_roster && !profile.assigned_agent_name && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Unassigned</span>
        )}
        {kycInfo && (
          <span style={{ fontSize: 11, fontWeight: 600, background: kycInfo.bg, color: kycInfo.color, borderRadius: 5, padding: '3px 9px' }}>
            {kycInfo.label}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Quick action buttons ─────────────────────────────────────────

interface QuickActionsProps {
  readonly clientId: string;
  readonly clientName: string;
}

function QuickActions({ clientId, clientName }: QuickActionsProps): JSX.Element {
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [taskFlash, setTaskFlash] = useState<string | null>(null);

  // "Statements" used to navigate to /agent/clients/{id}/statements
  // which has no React Route — produced a "No routes matched" warning
  // + a blank screen. The statements section is actually embedded on
  // this same page (see <details id="client-statements"> below), so the
  // right action is to open and scroll to it rather than navigate
  // anywhere. Same-tab, in-page; no other quick-action involves
  // navigation either when its data lives here.
  const scrollToStatements = (): void => {
    const el = document.getElementById('client-statements') as HTMLDetailsElement | null;
    if (!el) return;
    el.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // The page is mounted for agents and for managers; each role has its own screens, and
  // an agent's are refused to a manager. "Propose deposit" pointed at a route that never
  // existed: the deposits screen is where a deposit is proposed.
  const base = globalThis.location.pathname.startsWith('/manager') ? '/manager' : '/agent';
  // The call guide's own page (logging the call, the talking points in full) is reached from
  // here for both roles: a manager has no other way to it, since appointments list it for agents.
  const actions = [
    { label: 'Deposits', href: `${base}/deposits` },
    { label: 'Commitments', href: `${base}/commitments` },
    { label: 'Appointments', href: `${base}/appointments` },
    { label: 'Call guide', href: `${base}/call-guide/${clientId}` },
  ];

  const btnStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '7px 14px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.ink,
    textDecoration: 'none',
    transition: 'border-color 0.15s',
    cursor: 'pointer',
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {actions.map(a => (
          <a
            key={a.label}
            href={a.href}
            target="_blank"
            rel="noopener noreferrer"
            style={btnStyle}
          >
            {a.label}
            <span style={{ fontSize: 10, color: colors.textMuted }}>↗</span>
          </a>
        ))}
        {/* Same-page anchor — opens + scrolls to the embedded
            <details id="client-statements"> below. No new tab; no
            navigation; no ↗ glyph. */}
        <button
          type="button"
          onClick={scrollToStatements}
          style={btnStyle}
        >
          Statements
          {/* */}<span style={{ fontSize: 10, color: colors.textMuted }}>↓</span>
        </button>
        {/* In-place modal — doesn't open a new tab because the form is
            short and the agent stays in the same client context after. */}
        <button
          type="button"
          onClick={() => setTaskModalOpen(true)}
          style={{ ...btnStyle, color: colors.navy, borderColor: colors.navy }}
        >
          + New task
        </button>
        {/* Upselling (0070): the client reviews and signs it on their own. */}
        <button
          type="button"
          onClick={() => setProposeOpen(true)}
          style={{ ...btnStyle, color: colors.navy, borderColor: colors.navy }}
        >
          + Propose a Series
        </button>
        {taskFlash && (
          <span
            className="text-xs"
            style={{
              color: colors.success,
              padding: '6px 10px',
              borderRadius: 6,
              background: colors.successBg,
            }}
            aria-live="polite"
          >
            {taskFlash}
          </span>
        )}
      </div>

      {taskModalOpen && (
        <NewTaskModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setTaskModalOpen(false)}
          onCreated={() => {
            setTaskModalOpen(false);
            setTaskFlash('✓ Task created. It will appear on your Act-now queue when due.');
            globalThis.setTimeout(() => setTaskFlash(null), 5000);
          }}
        />
      )}

      {proposeOpen && (
        <ProposeProductModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setProposeOpen(false)}
          onCreated={() => {
            setProposeOpen(false);
            setTaskFlash('✓ Proposal sent. The client will see it on their dashboard.');
            globalThis.setTimeout(() => setTaskFlash(null), 5000);
          }}
        />
      )}
    </>
  );
}

// ─── Left sidebar ─────────────────────────────────────────────────

interface LeftSidebarProps {
  readonly summary: PortfolioSummary | null;
  readonly openCasesCount: number;
}

function LeftSidebar({ summary, openCasesCount }: LeftSidebarProps): JSX.Element {
  const ccy = summary?.upcoming_maturities[0]?.currency_code ?? 'MXN';
  // PORTAL-01: `summary.balances` is kept separate per currency — pick the
  // sidebar's display currency's row (see helper docstring).
  const balance = summary ? pickCurrencyBalance(summary.balances, ccy) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Summary at a glance */}
      <div style={{ background: gradients.navy, borderRadius: 12, padding: '16px 18px', color: '#fff' }}>
        <div style={{ fontSize: 10, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 2 }}>Total balance</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: colors.gold }}>
              {balance ? formatMoney(balance.total_balance, ccy) : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 2 }}>Results to date</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: colors.goldLight }}>
              {balance ? formatMoney(balance.accumulated_yield, ccy) : '—'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 2 }}>Active Series</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{summary?.active_products_count ?? '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, opacity: 0.55, marginBottom: 2 }}>Open cases</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{openCasesCount}</div>
          </div>
        </div>
      </div>

      {/* Upcoming maturities */}
      {summary && summary.upcoming_maturities.length > 0 && (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Terms ending</div>
          {summary.upcoming_maturities.slice(0, 3).map(m => (
            <div key={openTermKey(m)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
              <div>
                <div style={{ fontWeight: 500, color: colors.ink }}>{m.product_name}</div>
                <div style={{ fontSize: 11, color: colors.textMuted }}>{completeAfter(m.maturity_date)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 600, color: colors.navy }}>{formatMoney(m.amount, m.currency_code)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Commitment schedule */}
      {summary && summary.active_commitments_count > 0 && (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Commitments</div>
          <div style={{ fontSize: 13, color: colors.ink, fontWeight: 500 }}>{summary.active_commitments_count} active</div>
          {summary.next_commitment_date && (
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
              Next: {formatDate(summary.next_commitment_date)}
              {summary.next_commitment_amount && (
                <span> · {formatMoney(summary.next_commitment_amount, ccy)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Pinned interaction card ──────────────────────────────────────

interface PinnedCardProps {
  readonly interaction: Interaction;
  readonly onUnpin: (id: string) => void;
  readonly unpinning: boolean;
}

function PinnedCard({ interaction, onUnpin, unpinning }: PinnedCardProps): JSX.Element {
  return (
    <div style={{ background: `${colors.gold}10`, border: `1px solid ${colors.gold}44`, borderRadius: 10, padding: '12px 14px', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.ink }}>
          {kindLabel(interaction.kind)} — {outcomeLabel(interaction.outcome)}
        </div>
        <button
          type="button"
          onClick={() => onUnpin(interaction.id)}
          disabled={unpinning}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: colors.gold, padding: 0, lineHeight: 1, flexShrink: 0 }}
          title="Unpin"
        >
          ★
        </button>
      </div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: interaction.notes ? 6 : 0 }}>
        {formatDateTime(interaction.started_at)} · {interaction.actor_name ?? 'Unknown'}
      </div>
      {interaction.notes && (
        <div style={{ fontSize: 12, color: colors.inkSoft, fontStyle: 'italic', marginTop: 4 }}>
          &quot;{interaction.notes.slice(0, 200)}{interaction.notes.length > 200 ? '…' : ''}&quot;
        </div>
      )}
      {interaction.commitment_made && (
        <div style={{ marginTop: 6, fontSize: 11, color: colors.navy, fontWeight: 600 }}>
          Commitment: {interaction.commitment_made}
        </div>
      )}
      {interaction.next_action_required && (
        <div style={{ marginTop: 4, fontSize: 11, color: colors.warning }}>
          Next action: {interaction.next_action_required}
        </div>
      )}
    </div>
  );
}

// ─── Interaction row ──────────────────────────────────────────────

interface InteractionRowItemProps {
  readonly interaction: Interaction;
  readonly onPin: (id: string) => void;
  readonly pinning: boolean;
}

function InteractionRowItem({ interaction, onPin, pinning }: InteractionRowItemProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: `1px solid ${colors.border}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.ink }}>
            {kindLabel(interaction.kind)} — {outcomeLabel(interaction.outcome)}
          </span>
          <span style={{ fontSize: 11, color: colors.textMuted }}>
            {formatDateTime(interaction.started_at)} · {interaction.actor_name ?? 'Unknown'}
          </span>
        </div>
        {interaction.notes && (
          <div style={{ fontSize: 12, color: colors.inkSoft, fontStyle: 'italic' }}>
            &quot;{interaction.notes.slice(0, 120)}{interaction.notes.length > 120 ? '…' : ''}&quot;
          </div>
        )}
        {interaction.next_action_required && (
          <div style={{ marginTop: 4, fontSize: 11, color: colors.warning }}>
            Pending: {interaction.next_action_required}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onPin(interaction.id)}
        disabled={pinning}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, color: colors.border, padding: '0 2px', lineHeight: 1, flexShrink: 0, transition: 'color 0.15s' }}
        title="Pin as relationship context"
      >
        ☆
      </button>
    </div>
  );
}

// ─── Open cases list ──────────────────────────────────────────────

function caseStatusBg(status: string): { bg: string; color: string } {
  if (status === 'awaiting_review') return { bg: colors.infoBg, color: colors.infoText };
  if (status === 'in_progress') return { bg: colors.warningBg, color: colors.warning };
  return { bg: colors.surfaceMuted, color: colors.textMuted };
}

interface OpenCasesListProps {
  readonly cases: ClientCase[];
}

function OpenCasesList({ cases }: OpenCasesListProps): JSX.Element {
  if (cases.length === 0) {
    return <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>No open cases.</p>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {cases.map(c => {
        const style = caseStatusBg(c.status);
        return (
          <div key={c.case_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: colors.ink }}>{c.workflow_name}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>{humanize(c.current_state_slug)}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '2px 7px', textTransform: 'uppercase', background: style.bg, color: style.color }}>
              {humanize(c.status)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main content panel ───────────────────────────────────────────

interface MainContentProps {
  readonly pinned: Interaction[];
  readonly recent: Interaction[];
  readonly cases: ClientCase[];
  readonly onPin: (id: string) => void;
  readonly onUnpin: (id: string) => void;
  readonly busyId: string | null;
}

function MainContent({ pinned, recent, cases, onPin, onUnpin, busyId }: MainContentProps): JSX.Element {
  const cardStyle = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 14,
  };
  const headingStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: colors.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    marginBottom: 10,
    display: 'block',
  };

  return (
    <div>
      {/* Pinned interactions */}
      {pinned.length > 0 && (
        <div style={cardStyle}>
          <span style={headingStyle}>Pinned relationship context</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pinned.map(i => (
              <PinnedCard
                key={i.id}
                interaction={i}
                onUnpin={onUnpin}
                unpinning={busyId === i.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Open cases */}
      <div style={cardStyle}>
        <span style={headingStyle}>Open cases ({cases.length})</span>
        <OpenCasesList cases={cases} />
      </div>

      {/* Recent interactions */}
      <div style={cardStyle}>
        <span style={headingStyle}>Recent interactions</span>
        {recent.length === 0 ? (
          <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>No interactions yet.</p>
        ) : (
          recent.map(i => (
            <InteractionRowItem
              key={i.id}
              interaction={i}
              onPin={onPin}
              pinning={busyId === i.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Statements section (staff-only) ─────────────────────────────

function FormField({ label, children }: { readonly label: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: colors.textMuted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

interface StatementsSectionProps {
  readonly clientId: string;
  readonly products: ProductOption[];
}

function StatementsSection({ clientId, products }: StatementsSectionProps): JSX.Element {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterProductId, setFilterProductId] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [emailingId, setEmailingId] = useState<string | null>(null);
  const [emailedId, setEmailedId] = useState<string | null>(null);

  const yearOptions = useMemo(() => {
    const now = new Date().getUTCFullYear();
    return [now, now - 1, now - 2, now - 3, now - 4];
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: StatementFilters = {};
      if (filterYear) filters.year = Number(filterYear);
      if (filterMonth) filters.month = Number(filterMonth);
      if (filterProductId) filters.product_id = filterProductId;
      setStatements(await portfolioService.listClientStatements(clientId, filters));
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load statements.'));
    } finally {
      setLoading(false);
    }
  }, [clientId, filterYear, filterMonth, filterProductId]);

  useEffect(() => { reload(); }, [reload]);

  const handleDownload = async (id: string): Promise<void> => {
    setDownloadingId(id);
    setError(null);
    try {
      const url = await portfolioService.getStatementDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not generate the download link.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSendEmail = async (id: string): Promise<void> => {
    setEmailingId(id);
    setEmailedId(null);
    setError(null);
    try {
      await portfolioService.sendStatementEmail(id);
      setEmailedId(id);
      await reload();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not send the statement email.'));
    } finally {
      setEmailingId(null);
    }
  };

  const selectStyle = { padding: '7px 10px', borderRadius: 7, border: `1px solid ${colors.border}`, fontSize: 13, background: colors.surface };

  return (
    <div style={{ marginTop: 8 }}>
      {error && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger, fontSize: 13 }}>
          {error}
        </div>
      )}
      {emailedId && !emailingId && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.successText, fontSize: 13 }}>
          Statement email queued.
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14, padding: '12px 14px', borderRadius: 10, background: colors.canvas, border: `1px solid ${colors.border}` }}>
        <FormField label="Year">
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={selectStyle}>
            <option value="">Any year</option>
            {yearOptions.map(y => <option key={y} value={String(y)}>{y}</option>)}
          </select>
        </FormField>
        <FormField label="Month">
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={selectStyle}>
            <option value="">Any month</option>
            {MONTH_LABELS.map((l, i) => <option key={l} value={String(i + 1)}>{l}</option>)}
          </select>
        </FormField>
        <FormField label="Series">
          <select value={filterProductId} onChange={e => setFilterProductId(e.target.value)} style={selectStyle}>
            <option value="">Any Series</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </FormField>
        {(filterYear || filterMonth || filterProductId) && (
          <button
            type="button"
            onClick={() => { setFilterYear(''); setFilterMonth(''); setFilterProductId(''); }}
            style={{ padding: '7px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`, cursor: 'pointer', alignSelf: 'flex-end' }}
          >
            Clear
          </button>
        )}
      </div>

      {loading && <p style={{ fontSize: 13, color: colors.textMuted }}>Loading statements…</p>}
      {!loading && statements.length === 0 && (
        <p style={{ fontSize: 13, color: colors.textMuted }}>No statements found.</p>
      )}
      {!loading && statements.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${colors.border}` }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead style={{ background: colors.canvas }}>
              <tr>
                {['Period', 'Series', 'Closing balance', 'Movements', 'Generated', 'Emailed', ''].map(h => (
                  <th key={h} style={{ textAlign: h === 'Closing balance' || h === '' ? 'right' : 'left', padding: '8px 12px', fontWeight: 600, color: colors.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {statements.map((s, idx) => (
                <tr key={s.id} style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.border}` }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500, color: colors.ink }}>{MONTH_LABELS[s.period_month - 1]} {s.period_year}</td>
                  <td style={{ padding: '8px 12px', color: colors.inkSoft }}>{s.product_name}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: colors.ink }}>{formatMoney(s.closing_balance, s.currency_code)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: colors.textMuted }}>{s.movement_count}</td>
                  <td style={{ padding: '8px 12px', fontSize: 11, color: colors.textMuted }}>{formatDateTime(s.generated_at)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 11, color: s.email_sent_at ? colors.successText : colors.textMuted }}>{s.email_sent_at ? formatDateTime(s.email_sent_at) : '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" onClick={() => handleDownload(s.id)} disabled={downloadingId === s.id} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}`, cursor: 'pointer', opacity: downloadingId === s.id ? 0.6 : 1 }}>
                        {downloadingId === s.id ? '…' : 'Download'}
                      </button>
                      <button type="button" onClick={() => handleSendEmail(s.id)} disabled={emailingId === s.id} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: colors.navy, color: '#fff', border: 'none', cursor: 'pointer', opacity: emailingId === s.id ? 0.6 : 1 }}>
                        {emailingId === s.id ? 'Sending…' : 'Email'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 12, color: colors.textMuted }}>
        Statements are produced by the monthly run and emailed to the client. There is no
        on-demand generator: rendering the same period twice against a ledger that moved in
        between would produce two documents that disagree (0074).
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────

export default function ClientDetail(): JSX.Element {
  const { clientId } = useParams<{ clientId: string }>();

  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [openCases, setOpenCases] = useState<ClientCase[]>([]);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadAll = useCallback(async (id: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const [profileRes, summaryRes, casesRes, interRes, balancesRes] = await Promise.allSettled([
        staffService.getClientProfile(id),
        portfolioService.getClientSummary(id),
        interactionsService.listOpenCases(id),
        interactionsService.listForClient(id, 20),
        portfolioService.getClientBalance(id),
      ]);
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      if (casesRes.status === 'fulfilled') setOpenCases(casesRes.value);
      if (interRes.status === 'fulfilled') setInteractions(interRes.value);
      // The statements to ask for are those of the products THIS client holds.
      if (balancesRes.status === 'fulfilled') {
        setProducts(
          productOptions(balancesRes.value.items.map((b) => ({ id: b.product_id, name: b.product_name }))),
        );
      }
      if (profileRes.status === 'rejected') {
        setLoadError(getApiErrorMessage(profileRes.reason, 'Could not load client profile.'));
      }
    } catch (e: unknown) {
      setLoadError(getApiErrorMessage(e, 'Could not load client.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (clientId) loadAll(clientId);
  }, [clientId, loadAll]);

  const refreshInteractions = useCallback(async () => {
    if (!clientId) return;
    const updated = await interactionsService.listForClient(clientId, 20);
    setInteractions(updated);
  }, [clientId]);

  const handlePin = useCallback(async (interactionId: string): Promise<void> => {
    setBusyId(interactionId);
    try {
      await interactionsService.pin(interactionId, true);
      await refreshInteractions();
    } finally {
      setBusyId(null);
    }
  }, [refreshInteractions]);

  const handleUnpin = useCallback(async (interactionId: string): Promise<void> => {
    setBusyId(interactionId);
    try {
      await interactionsService.pin(interactionId, false);
      await refreshInteractions();
    } finally {
      setBusyId(null);
    }
  }, [refreshInteractions]);

  if (!clientId) {
    return <p style={{ color: colors.danger, padding: 24 }}>No client ID in URL.</p>;
  }

  if (loading) {
    return (
      <div style={{ padding: 32, color: colors.textMuted, fontSize: 14 }}>Loading client…</div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ padding: '12px 16px', borderRadius: 8, background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger, fontSize: 14 }}>
          {loadError}
        </div>
      </div>
    );
  }

  const pinnedInteractions = interactions.filter(i => i.is_pinned);
  const recentInteractions = interactions.filter(i => !i.is_pinned).slice(0, 10);
  const lastInteraction = interactions[0] ?? null;
  const fullName = profile ? `${profile.first_name} ${profile.last_name}`.trim() : 'Client';

  const gridColumns = panelOpen ? '260px 1fr 340px' : '260px 1fr';

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 16px 80px' }}>
      <PageHeader title="Client 360" subtitle={profile?.email ?? undefined} />

      {profile && <IdentityHeader profile={profile} />}
      <QuickActions
        clientId={clientId}
        clientName={
          profile
            ? `${profile.first_name} ${profile.last_name}`.trim() || profile.email
            : 'this client'
        }
      />

      {/* Manager-only compliance controls (freeze / suspension / risk band).
          Self-gates by role, so agents never see it. */}
      <ClientComplianceActions clientId={clientId} />

      {/* A client on this agent's roster: record what they said about a transfer (owner,
          2026-10-31). It moves no money; operations attribute transfers. */}
      <AgentDepositNotice clientId={clientId} onRoster={profile?.is_on_roster === true} />

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 18, alignItems: 'start' }}>

        {/* Left sidebar */}
        <LeftSidebar summary={summary} openCasesCount={openCases.length} />

        {/* Main content */}
        <MainContent
          pinned={pinnedInteractions}
          recent={recentInteractions}
          cases={openCases}
          onPin={id => handlePin(id)}
          onUnpin={id => handleUnpin(id)}
          busyId={busyId}
        />

        {/* Call guide panel */}
        {panelOpen && (
          <CallGuidePanel
            clientId={clientId}
            clientName={fullName}
            summary={summary}
            openCases={openCases}
            lastInteraction={lastInteraction}
            onCallLogged={() => refreshInteractions()}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>

      {/* Statements (collapsible). The id is used by the QuickActions
          "Statements" button to scroll + open this section in-place
          instead of navigating to a nonexistent /statements page. */}
      <details
        id="client-statements"
        style={{ marginTop: 24, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}
      >
        <summary style={{ padding: '12px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: colors.ink, background: colors.canvas, listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Monthly statements</span>
          <span style={{ fontSize: 11, color: colors.textMuted }}>expand ▼</span>
        </summary>
        <div style={{ padding: '16px', background: colors.surface }}>
          <NavCorrectionsCard clientId={clientId} />
          <StatementsSection clientId={clientId} products={products} />
        </div>
      </details>

      {/* Call guide toggle (fixed) */}
      <button
        type="button"
        onClick={() => setPanelOpen(v => !v)}
        style={{
          position: 'fixed',
          bottom: 28,
          right: 28,
          padding: '10px 20px',
          borderRadius: 50,
          border: 'none',
          background: panelOpen ? colors.dangerBg : gradients.navy,
          color: panelOpen ? colors.danger : '#fff',
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
          boxShadow: '0 4px 18px rgba(6,9,26,0.28)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
        }}
      >
        {panelOpen ? '✕ Close call guide' : '📞 Call guide'}
      </button>
    </div>
  );
}
